import React, { FC, useContext, useEffect, useMemo, useState } from "react";

import {
	Campaign as CampaignIcon,
	CloseOutlined as CloseOutlinedIcon,
	ConstructionOutlined as ConstructionOutlinedIcon,
	EventNoteOutlined as EventNoteOutlinedIcon,
	FolderZipOutlined as FolderZipOutlinedIcon,
	Inventory2Outlined as Inventory2OutlinedIcon,
	InventoryOutlined as InventoryOutlinedIcon,
	MenuOutlined as MenuOutlinedIcon,
	NewspaperOutlined as NewspaperOutlinedIcon,
	PublishedWithChangesOutlined as PublishedWithChangesOutlinedIcon,
	SchoolOutlined as SchoolOutlinedIcon,
	ScienceOutlined as ScienceOutlinedIcon,
} from "@mui/icons-material";
import {
	Badge,
	Box,
	Divider,
	Drawer,
	IconButton,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Paper,
	Tooltip,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import * as Sentry from "@sentry/react";

import LogoWhite from "@/assets/svg/logo/logo-satos-white.svg?react";

import { SATOSLink } from "@/components/Application/SATOSLink";
import { Translate } from "@/components/Application/Translate";

import { Procedure } from "@/model/Procedure/Procedure";
import { UserContext } from "@/model/User/UserContext";

import { useQueryParamHide } from "@/service/Hooks";
import { useFetchAllLeftSidebarMenuItems } from "@/service/MenuQueries";
import { useFetchSystemSettings } from "@/service/Settings/SettingsQueries";
import { useFetchProcedureUpdateSuggestions } from "@/service/TaskQueries";

/* ────────────────────────────────────────────────────────── */
/* Design tokens                                              */
/* ────────────────────────────────────────────────────────── */
const DRAWER_WIDTH_EXPANDED = 232;
const DRAWER_WIDTH_COLLAPSED = 76;
const APP_BAR_HEIGHT = 50; // <- TopBar height
const BRAND_RED = "#D32F2F";
const BRAND_RED_DARK = "#B72A2A";

/* ────────────────────────────────────────────────────────── */
/* Icon pill + badges                                         */
/* ────────────────────────────────────────────────────────── */
const iconGradient = (id: string): [string, string] => {
	switch (id) {
		case "hide_proc":
			return ["#D9EAFE", "#EDF4FF"];
		case "hide_archive":
		case "archive":
			return ["#DDEBFF", "#F1F7FF"];
		case "hide_competance":
			return ["#FFE2DB", "#FFF0EC"];
		case "hide_task":
			return ["#EADCF7", "#F5ECFD"];
		case "hide_lab":
			return ["#F8DADA", "#FCEDED"];
		case "hide_materialhanding":
			return ["#FFE1D3", "#FFF0E8"];
		case "hide_info":
			return ["#FFEBC7", "#FFF6DE"];
		case "hide_shift":
			return ["#D9F4E6", "#ECFAF2"];
		case "custumlink1":
			return ["#E7E2DE", "#F2EEEB"];
		default:
			return ["#EDEDED", "#F7F7F7"];
	}
};

const IconPill: FC<{
	id: string;
	badge?: number;
	children: React.ReactNode;
}> = ({ id, badge, children }) => {
	const [c1, c2] = iconGradient(id);

	return (
		<Badge
			invisible={!(typeof badge === "number" && badge > 0)}
			badgeContent={badge}
			max={99}
			overlap="circular"
			color="error"
			anchorOrigin={{ vertical: "top", horizontal: "right" }}
			slotProps={{
				badge: {
					sx: (t) => ({
						top: 0,
						right: 0,
						transform: "translate(40%, -40%)",
						fontSize: 12,
						fontWeight: 800,
						lineHeight: "20px",
						minWidth: 22,
						height: 22,
						px: 0.75,
						borderRadius: 11,
						color: t.palette.common.white,
						backgroundColor: t.palette.error.main,
						border: `2px solid ${t.palette.background.paper}`,
						boxShadow: "0 2px 6px rgba(0,0,0,.12)",
					}),
				},
			}}
			sx={{ "& .MuiBadge-badge": { letterSpacing: 0.2 } }}
		>
			<Box
				sx={(t) => ({
					width: 36,
					height: 36,
					borderRadius: 1.5,
					display: "grid",
					placeItems: "center",
					position: "relative",
					background: `linear-gradient(135deg, ${c1} 0%, ${c2} 100%)`,
					border: `1px solid ${t.palette.divider}`,
					boxShadow: "inset 0 1px 0 rgba(255,255,255,.5)",
					color: t.palette.text.primary,
					"& svg": { color: "inherit" },
				})}
			>
				{children}
			</Box>
		</Badge>
	);
};

/* ────────────────────────────────────────────────────────── */
/* Icons per menu id                                          */
/* ────────────────────────────────────────────────────────── */
const getNavItemIcon = (
	id: string,
	isProcedureSuggestionsAdministrator: boolean,
	suggestionsToVerify: number,
	suggestionsToVerifyUser: number,
) => {
	const badge =
		id === "hide_proc"
			? isProcedureSuggestionsAdministrator
				? suggestionsToVerify
				: suggestionsToVerifyUser
			: undefined;

	switch (id) {
		case "hide_proc":
			return (
				<IconPill id={id} badge={badge}>
					<InventoryOutlinedIcon fontSize="small" />
				</IconPill>
			);
		case "hide_archive":
		case "archive":
			return (
				<IconPill id={id}>
					<Inventory2OutlinedIcon fontSize="small" />
				</IconPill>
			);
		case "hide_competance":
			return (
				<IconPill id={id}>
					<SchoolOutlinedIcon fontSize="small" />
				</IconPill>
			);
		case "hide_task":
			return (
				<IconPill id={id}>
					<EventNoteOutlinedIcon fontSize="small" />
				</IconPill>
			);
		case "hide_lab":
			return (
				<IconPill id={id}>
					<ScienceOutlinedIcon fontSize="small" />
				</IconPill>
			);
		case "hide_materialhanding":
			return (
				<IconPill id={id}>
					<ConstructionOutlinedIcon fontSize="small" />
				</IconPill>
			);
		case "hide_info":
			return (
				<IconPill id={id}>
					<FolderZipOutlinedIcon fontSize="small" />
				</IconPill>
			);
		case "hide_shift":
			return (
				<IconPill id={id}>
					<PublishedWithChangesOutlinedIcon fontSize="small" />
				</IconPill>
			);
		case "custumlink1":
			return (
				<IconPill id={id}>
					<NewspaperOutlinedIcon fontSize="small" />
				</IconPill>
			);
		default:
			return (
				<IconPill id={id}>
					<InventoryOutlinedIcon fontSize="small" />
				</IconPill>
			);
	}
};

/* ────────────────────────────────────────────────────────── */
/* Sidebar                                                    */
/* ────────────────────────────────────────────────────────── */
export const SidebarPro: FC = () => {
	const theme = useTheme();
	const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

	// collapsed is desktop-only; mobile drawer is always expanded
	const [isCollapsed, setIsCollapsed] = useState<boolean>(true);
	const [mobileOpen, setMobileOpen] = useState<boolean>(false);
	const [hide, setHide] = useState<boolean>(false);

	const menu = useFetchAllLeftSidebarMenuItems();
	const items = Array.isArray(menu) ? menu : [];

	const { data: systemSettings, isLoading: isLoadingSystemSettings } =
		useFetchSystemSettings();
	const isUserFeedBackActive = Boolean(
		!isLoadingSystemSettings &&
			systemSettings &&
			systemSettings.IsFeedbackEnabled,
	);

	const { data: suggestionsData } = useFetchProcedureUpdateSuggestions(false);
	const user = useContext(UserContext);
	const isProcedureSuggestionsAdministrator =
		user?.IsProcedureSuggestionsAdministrator;

	const suggestionsToVerify = useMemo(
		() =>
			suggestionsData
				? countStatusForSuggestion(suggestionsData, "Unverified")
				: 0,
		[suggestionsData],
	);
	const suggestionsToVerifyUser = useMemo(
		() =>
			suggestionsData && user?.Id
				? countSuggestionsByUser(suggestionsData, user.Id)
				: 0,
		[suggestionsData, user?.Id],
	);

	const queryHide = useQueryParamHide({ overrideForSpecificPages: true });
	useEffect(() => setHide(queryHide), [queryHide]);

	const openDrawerMobile = () => {
		setMobileOpen(true);
		setIsCollapsed(false); // ensure expanded content on mobile
	};
	const closeDrawerMobile = () => setMobileOpen(false);

	/* ───────── shared menu content ───────── */
	const menuContent = (
		<Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
			<List sx={{ py: 1 }}>
				{items.map((item) => {
					const labelNode = (
						<Translate String={`SideBar.${item.Title}`}>{item.Title}</Translate>
					);

					const itemButton = (
						<SATOSLink
							key={item.Id}
							to={item.Url}
							style={{ textDecoration: "none", color: "inherit" }}
						>
							<ListItem disablePadding sx={{ display: "block" }}>
								<ListItemButton
									onClick={() => {
										if (isSmall) closeDrawerMobile();
									}}
									sx={{
										minHeight: 46,
										borderRadius: 1.5,
										mx: 1,
										mb: 0.5,
										justifyContent: isCollapsed ? "center" : "flex-start",
										px: isCollapsed ? 1.25 : 1.5,
										"&:hover": {
											backgroundColor: (t) =>
												isCollapsed
													? t.palette.action.hover
													: t.palette.action.selected,
										},
									}}
								>
									<ListItemIcon
										sx={{
											minWidth: 0,
											mr: isCollapsed ? 0 : 1.5,
											justifyContent: "center",
										}}
									>
										{getNavItemIcon(
											item.Id,
											Boolean(isProcedureSuggestionsAdministrator),
											suggestionsToVerify,
											suggestionsToVerifyUser,
										)}
									</ListItemIcon>

									{!isCollapsed && (
										<ListItemText
											primary={labelNode}
											primaryTypographyProps={{ fontWeight: 600 }}
											sx={{ opacity: 1, ml: 1 }}
										/>
									)}
								</ListItemButton>
							</ListItem>
						</SATOSLink>
					);

					return isCollapsed ? (
						<Tooltip
							key={item.Id}
							title={labelNode}
							placement="right"
							enterDelay={150}
							arrow
							slotProps={{
								popper: {
									sx: {
										"& .MuiTooltip-tooltip": {
											fontSize: "0.95rem",
											fontWeight: 500,
											px: 1.25,
											py: 0.75,
											borderRadius: 1,
										},
									},
								},
							}}
						>
							<span>{itemButton}</span>
						</Tooltip>
					) : (
						itemButton
					);
				})}
			</List>

			{/* Bottom region */}
			<Box sx={{ mt: "auto", p: 1.25 }}>
				{isUserFeedBackActive && (
					<>
						{!isCollapsed ? (
							<Paper
								variant="outlined"
								sx={{ borderRadius: 2, px: 1.5, py: 1.25, mb: 1.25 }}
							>
								<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
									<Box
										sx={(t) => ({
											width: 36,
											height: 36,
											borderRadius: 1.5,
											display: "grid",
											placeItems: "center",
											background: t.palette.info.light,
											color: t.palette.info.contrastText,
										})}
									>
										<CampaignIcon fontSize="small" />
									</Box>

									<Box sx={{ flex: 1, minWidth: 0 }}>
										<Typography fontWeight={800} lineHeight={1.15}>
											<Translate String="SideBar.ReportBug">
												Report Bug
											</Translate>
										</Typography>
										<Typography variant="caption" color="text.secondary">
											<Translate>Tell us what broke</Translate>
										</Typography>
									</Box>

									<IconButton
										size="small"
										onClick={async () => {
											const feedback = Sentry.getFeedback();
											const form = await feedback?.createForm();
											if (form) {
												form.appendToDom();
												form.open();
											}
										}}
									>
										<CampaignIcon />
									</IconButton>
								</Box>
							</Paper>
						) : (
							<Tooltip
								title={<Translate>Report Bug</Translate>}
								placement="right"
								arrow
							>
								<IconButton
									onClick={async () => {
										const feedback = Sentry.getFeedback();
										const form = await feedback?.createForm();
										if (form) {
											form.appendToDom();
											form.open();
										}
									}}
									sx={{
										mx: "auto",
										display: "block",
										bgcolor: "background.paper",
										border: (t) => `1px dashed ${t.palette.divider}`,
										width: 40,
										height: 40,
										borderRadius: 1.5,
									}}
								>
									<CampaignIcon fontSize="small" />
								</IconButton>
							</Tooltip>
						)}
					</>
				)}
			</Box>
		</Box>
	);

	if (hide) return null;

	return (
		<>
			{/* Mobile: floating burger ONLY when the drawer is closed */}
			{isSmall && !mobileOpen && (
				<IconButton
					aria-label="open menu"
					onClick={openDrawerMobile}
					sx={{
						position: "fixed",
						left: 12,
						top: `calc(${APP_BAR_HEIGHT}px / 2 - 20px)`, // centered in the 50px app bar
						width: 40,
						height: 40,
						p: 0,
						zIndex: (t) => t.zIndex.appBar + 1,
						bgcolor: "background.paper",
						border: (t) => `1px solid ${t.palette.divider}`,
						borderRadius: 1.25, // less roundy, matches the rest
						boxShadow: 1,
						"& .MuiSvgIcon-root": { fontSize: 22 },
						"&:hover": { boxShadow: 2, bgcolor: (t) => t.palette.action.hover },
						"&:focus-visible": {
							outline: (t) => `2px solid ${t.palette.primary.main}`,
							outlineOffset: 2,
						},
					}}
				>
					<MenuOutlinedIcon />
				</IconButton>
			)}

			{/* Mobile Drawer (brand red header) */}
			<Drawer
				anchor="left"
				open={mobileOpen}
				onClose={closeDrawerMobile}
				ModalProps={{ keepMounted: true }}
				PaperProps={{ sx: { width: 300 } }}
			>
				<Box
					sx={(t) => ({
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						px: 1.5,
						py: 1.25,
						background: `linear-gradient(180deg, ${BRAND_RED} 0%, ${BRAND_RED_DARK} 100%)`,
						color: t.palette.common.white,
						boxShadow: "0 6px 18px rgba(0,0,0,.12)",
					})}
				>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<LogoWhite width="84" />
					</Box>
					<IconButton
						aria-label="close menu"
						onClick={closeDrawerMobile}
						sx={{ color: "inherit" }}
					>
						<CloseOutlinedIcon />
					</IconButton>
				</Box>
				{/* <Divider /> */}
				{menuContent}
			</Drawer>

			{/* Desktop: permanent drawer */}
			{!isSmall && (
				<Drawer
					variant="permanent"
					sx={{
						width: isCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH_EXPANDED,
						flexShrink: 0,
						"& .MuiDrawer-paper": {
							width: isCollapsed
								? DRAWER_WIDTH_COLLAPSED
								: DRAWER_WIDTH_EXPANDED,
							boxSizing: "border-box",
							borderRight: (t) => `1px solid ${t.palette.divider}`,
						},
					}}
				>
					{/* Desktop brand header */}
					<Box
						sx={(t) => ({
							display: "flex",
							alignItems: "center",
							justifyContent: isCollapsed ? "center" : "space-between",
							height: `${APP_BAR_HEIGHT}px`, // exact 50px
							px: isCollapsed ? 1 : 1.25,
							background: isCollapsed
								? t.palette.background.paper
								: `linear-gradient(180deg, ${BRAND_RED} 0%, ${BRAND_RED_DARK} 100%)`,
							color: isCollapsed
								? t.palette.text.primary
								: t.palette.common.white,
							borderBottom: `1px solid ${isCollapsed ? t.palette.divider : alpha("#000", 0.08)}`,
						})}
					>
						{/* Logo is removed as a home link when collapsed (requested) */}
						{!isCollapsed && (
							<Box
								onClick={() => (window.location.href = "/")}
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1.25,
									cursor: "pointer",
									userSelect: "none",
									"&:active": { transform: "translateY(1px)" },
								}}
								aria-label="Go to home"
								role="link"
							>
								<LogoWhite width="84" />
							</Box>
						)}

						<Tooltip
							title={isCollapsed ? "Expand" : "Collapse"}
							arrow
							disableInteractive
						>
							<IconButton
								aria-label={
									isCollapsed ? "Expand navigation" : "Collapse navigation"
								}
								onClick={() => setIsCollapsed((v) => !v)}
								sx={(t) => ({
									color: isCollapsed
										? t.palette.text.primary
										: t.palette.common.white,
									width: 36,
									height: 36,
									borderRadius: 10,
									backgroundColor: isCollapsed
										? "transparent"
										: alpha("#fff", 0.12),
									boxShadow: isCollapsed ? "none" : "0 2px 8px rgba(0,0,0,.18)",
									transition: "all .18s ease",
									"&:hover": {
										backgroundColor: isCollapsed
											? alpha(t.palette.text.primary, 0.06)
											: alpha("#fff", 0.18),
										boxShadow: isCollapsed
											? "none"
											: "0 4px 12px rgba(0,0,0,.22)",
									},
									"&:focus-visible": {
										outline: `2px solid ${isCollapsed ? t.palette.primary.main : "#fff"}`,
										outlineOffset: 2,
									},
								})}
							>
								<MenuOutlinedIcon />
							</IconButton>
						</Tooltip>
					</Box>

					{/* <Divider /> */}
					{menuContent}
				</Drawer>
			)}
		</>
	);
};

/* ────────────────────────────────────────────────────────── */
/* Helpers for suggestions                                    */
/* ────────────────────────────────────────────────────────── */
export default SidebarPro;

export function countStatusForSuggestion(
	procedures: Procedure[] | any,
	status: string,
	userId: number | null = null,
): number {
	if (!Array.isArray(procedures)) return 0;
	let count = 0;
	procedures.forEach((procedure) => {
		procedure.Checklists?.forEach((checklist: any) => {
			checklist.ProcedureUpdateSuggestions?.forEach((suggestion: any) => {
				if (
					(userId === null || suggestion.CreatedBy.Id === userId) &&
					suggestion.Status === status
				) {
					count++;
				}
			});
		});
	});
	return count;
}

export function countSuggestionsByUser(
	data: Procedure[] | any,
	userId: number,
): number {
	if (!Array.isArray(data) || !userId) return 0;
	let count = 0;
	data.forEach((procedure) => {
		procedure.Checklists?.forEach((checklist: any) => {
			checklist.ProcedureUpdateSuggestions?.forEach((suggestion: any) => {
				if (suggestion.CreatedBy.Id === userId) count++;
			});
		});
	});
	return count;
}
